import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { FormSection, FormActions } from './ui';
import { useFormValidation } from '../hooks/useFormValidation';
import api from '../utils/api';

const UserEditForm = ({ user, onSubmit, onCancel }) => {
  const { t } = useTranslation();

  const initialData = {
    firstName: '',
    lastName: '',
    email: '',
    role: 'member',
    membershipPlan: '',
    isActive: true
  };

  const validationRules = {
    firstName: [
      (value) => !value?.trim() ? t('forms:validation.firstName.required') : null,
      (value) => value?.length > 50 ? t('forms:validation.firstName.tooLong') : null
    ],
    lastName: [
      (value) => !value?.trim() ? t('forms:validation.lastName.required') : null,
      (value) => value?.length > 50 ? t('forms:validation.lastName.tooLong') : null
    ],
    email: [
      (value) => !value?.trim() ? t('forms:validation.email.required') : null,
      (value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? t('forms:validation.email.invalid') : null
    ],
    role: [
      (value) => !['member', 'trainer', 'admin'].includes(value) ? t('forms:validation.role.invalid') : null
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    handleSubmit,
    setFormData,
    getFieldError,
    hasFieldError
  } = useFormValidation(initialData, validationRules);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [showPlanChangeDialog, setShowPlanChangeDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [originalPlanId, setOriginalPlanId] = useState('');

  useEffect(() => {
    if (user) {
      const planId = user.membershipPlan?._id || '';
      setOriginalPlanId(planId);
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || 'member',
        membershipPlan: planId,
        isActive: user.isActive !== undefined ? user.isActive : true
      });
    }
    fetchPlans();
  }, [user, setFormData]);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await api.get('/plans');
      setPlans(response.data.data || []);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setPlans([]); // Set empty array on error to prevent crashes
    } finally {
      setPlansLoading(false);
    }
  };

  const onFormSubmit = async (data) => {
    const submitData = {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.trim(),
      role: data.role,
      membershipPlan: data.membershipPlan || null,
      isActive: data.isActive
    };

    // Check if membership plan has changed
    const planChanged = (data.membershipPlan || '') !== originalPlanId;

    if (planChanged) {
      // Show confirmation dialog
      setPendingSubmitData(submitData);
      setShowPlanChangeDialog(true);
    } else {
      // No plan change, submit directly
      await onSubmit(submitData);
    }
  };

  const getSelectedPlanHasInstantActivation = () => {
    const selectedPlanId = formData.membershipPlan || '';
    if (!selectedPlanId) return false;
    const plan = plans.find(p => p._id === selectedPlanId);
    return plan?.allowInstantNextPlanActivation || false;
  };

  const userHasNextPlan = () => {
    return user?.nextPlan != null;
  };

  const handleConfirmPlanChange = async () => {
    setShowPlanChangeDialog(false);
    if (pendingSubmitData) {
      await onSubmit(pendingSubmitData);
      setPendingSubmitData(null);
    }
  };

  const handleCancelPlanChange = () => {
    setShowPlanChangeDialog(false);
    setPendingSubmitData(null);
  };

  const getSelectedPlanName = () => {
    const selectedPlanId = formData.membershipPlan || '';
    if (!selectedPlanId) return 'No Plan';
    const plan = plans.find(p => p._id === selectedPlanId);
    return plan ? plan.name : 'Unknown Plan';
  };

  const getOriginalPlanName = () => {
    if (!originalPlanId) return 'No Plan';
    const plan = plans.find(p => p._id === originalPlanId);
    return plan ? plan.name : 'Unknown Plan';
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Basic Information Section */}
      <FormSection 
        title="User Information" 
        description="Edit user account details and settings"
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="First Name"
              required
              value={formData.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              placeholder="Enter first name"
              error={hasFieldError('firstName')}
              helperText={getFieldError('firstName')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Last Name"
              required
              value={formData.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              placeholder="Enter last name"
              error={hasFieldError('lastName')}
              helperText={getFieldError('lastName')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              type="email"
              label="Email Address"
              required
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="Enter email address"
              error={hasFieldError('email')}
              helperText={getFieldError('email')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required error={hasFieldError('role')}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value)}
                label="Role"
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="trainer">Trainer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
              {getFieldError('role') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {getFieldError('role')}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth disabled={plansLoading}>
              <InputLabel>Membership Plan</InputLabel>
              <Select
                value={formData.membershipPlan || ''}
                onChange={(e) => updateField('membershipPlan', e.target.value)}
                label="Membership Plan"
              >
                <MenuItem value="">
                  <em>No Plan</em>
                </MenuItem>
                {plans.map((plan) => (
                  <MenuItem key={plan._id} value={plan._id}>
                    {plan.name} - ${plan.price} ({plan.duration} days)
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {plansLoading && <CircularProgress size={14} />}
                {plansLoading ? 'Loading plans...' : 'Optional - Select a membership plan'}
              </Typography>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => updateField('isActive', e.target.checked)}
                  />
                }
                label="Account is active"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                {formData.isActive 
                  ? 'User can log in and access the system' 
                  : 'User cannot log in to the system'
                }
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </FormSection>

      {/* User Details Section (if editing existing user) */}
      {user && (
        <FormSection 
          title="User Details" 
          description="Read-only information about this user"
          variant="card"
          collapsible
          defaultExpanded={false}
        >
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">User ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{user._id}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Username</Typography>
                <Typography variant="body1">{user.username}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Created</Typography>
                <Typography variant="body1">{new Date(user.createdAt).toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                <Typography variant="body1">{new Date(user.updatedAt).toLocaleString()}</Typography>
              </Box>
            </Grid>
          </Grid>
        </FormSection>
      )}

      {/* Form Actions */}
      <FormActions
        primaryAction={{
          label: 'Update User',
          loading: isSubmitting,
          disabled: isSubmitting
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: onCancel,
          disabled: isSubmitting
        }}
        alignment="right"
        variant="sticky"
      />

      {/* Plan Change Confirmation Dialog */}
      <Dialog
        open={showPlanChangeDialog}
        onClose={handleCancelPlanChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <WarningIcon />
          Membership Plan Change Warning
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Changing the membership plan will automatically update the following:
          </Alert>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
            Plan Change:
          </Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              From: <strong>{getOriginalPlanName()}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To: <strong>{getSelectedPlanName()}</strong>
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            The following will be automatically updated:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <PhotoCameraIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Plan Snapshot"
                secondary="A new snapshot will be created with current plan details"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <EventIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Membership Start Date"
                secondary="Will be set to today's date"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CalendarIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Membership End Date"
                secondary="Will be calculated based on the new plan's duration"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ScheduleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Next Plan Fields"
                secondary={
                  getSelectedPlanName() === 'No Plan'
                    ? "All next plan fields will be cleared (user must repurchase next plan)"
                    : getSelectedPlanHasInstantActivation() && userHasNextPlan()
                    ? "User's queued next plan will be CANCELLED to prevent overwriting this assignment"
                    : "Next plan start date will be adjusted to new membership end date if user has a queued next plan"
                }
              />
            </ListItem>
          </List>

          {/* Instant Activation Warning */}
          {getSelectedPlanHasInstantActivation() && userHasNextPlan() && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {t('admin:planAssignment.instantActivationWarning', 'WARNING: Instant Activation Plan')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('admin:planAssignment.nextPlanWillBeCleared',
                  'This plan allows instant activation. The user\'s queued next plan will be cancelled to prevent it from overwriting your assignment when payment is confirmed.')}
              </Typography>
              {user?.nextPlan && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {t('admin:planAssignment.queuedNextPlan', 'Queued next plan')}: <strong>{user.nextPlan.name || 'Unknown Plan'}</strong>
                  {user.nextPlanStartDate && ` (scheduled for ${new Date(user.nextPlanStartDate).toLocaleDateString()})`}
                </Typography>
              )}
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> This action cannot be undone. The user's previous membership dates and plan snapshot will be replaced.
              {getSelectedPlanName() === 'No Plan' && (
                <span> If user has a queued next plan, it will be cancelled.</span>
              )}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelPlanChange} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmPlanChange} variant="contained" color="warning" autoFocus>
            Confirm Plan Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserEditForm;